import {useCallback, useEffect, useMemo, useState, ReactNode, CSSProperties} from 'react'
import {useRouter} from 'next/router'
import BoundLink from '../../../components/bound-link'
import {getCookie, setCookie} from 'cookies-next'
import _ from 'lodash'
import type {GetServerSidePropsContext, GetServerSidePropsResult} from 'next'
// Contexts
import {useBallotContext} from '../../../context/ballot'
import {useVoterContext} from '../../../context/voter'
// Custom hooks
import {useBoundRoute} from '../../../hooks/use-bound-route'
// Animations
import AnimationLinearFade from '../../../components/animations/animation-linear-fade'
import ClientHead from '../../../components/client-head'
import ClientMeta from '../../../components/client-meta'
// Layouts
import ModalContainer from '../../../components/layout/modal-container'
import PagePasswordInput from '../../../components/layout/page-password-input'
import PageSimplePage from '../../../components/layout/page-simple-page'
// Components
import InterstitialAd from '../../../components/ads/interstitial-ad'
import PhotoGallery from '../../../components/photo-gallery/photo-gallery'
import LoadingIndicator from '../../../components/loading-indicator'
import Navbar from '../../../components/navbar'
import SponsoredFooter from '../../../components/sponsored-footer'
// Utils
import {calculateNextAdByPool} from '../../../utils/array-helpers'
import {
  accessBallotIsPublic,
  accessBallotIsPublished,
  accessBallotDisplayType,
} from '../../../utils/ballot-access'
import {
  calculateBallotPassword,
  calculateDisplayAdPoolByBallotAndQuestion,
  calculateGroupByBallotAndGroupSlug,
  calculateHasOnlyOneGroup,
  calculateSponsorshipAdPoolByBallot,
} from '../../../utils/ballot-calculators'
import {recordAdAndPageViews} from '../../../utils/analytics'
import {convertHoursToSeconds} from '../../../utils/number-helpers'
import {loadBallotByHost} from '../../../utils/prerender-helpers'
import {makeQuestionsForDisplay, seedInterstitialAdsByBallotfulGroup} from '../../../utils/question-helpers'
import shuffle from '../../../utils/shuffle'
import slugify from '../../../utils/slugify'
import {removeTrailingSlash} from '../../../utils/string-helpers'
// Consts
import {backupBallot} from '../../../consts/ballots'
// Styles
import pageStyles from '../../../components/layout/page.module.scss'
// Types
import type {
  Ballot,
  CategoryAd,
  ParentCategory,
  SeededDestination,
  SponsorshipAd,
} from '../../../types/ballot'

import type {Customer} from '../../../types/customer'

const calculateDestinationByDestinationsAndIdentifier = (
  destinations: SeededDestination[], identifier: string | undefined,
): SeededDestination | undefined => {
  if (!identifier) return
  const currentPageIsAd = identifier.startsWith('--ad-')

  if (currentPageIsAd) {
    const adIdentifier = identifier.slice(5)
    const matchedAds = destinations.filter(destination => destination.type === 'ad' && destination?._id === adIdentifier)
    if (matchedAds.length !== 1) return
    return matchedAds[0]
  }

  const matchedQuestions = destinations.filter(destination => destination.type === 'question' && destination.slug === identifier)
  if (matchedQuestions.length !== 1) return
  return matchedQuestions[0]
}

interface QuestionShowProps {
  initBasicBallot: Ballot
  dataFetchingError?: {message: string} | null
}

interface InitializeDestinationStateParams {
  ballot: any
  currentGroup: ParentCategory | null
  destination: SeededDestination | undefined
  analyticsSponsorshipAds: SponsorshipAd[]
}

const QuestionShow = ({
  initBasicBallot,
  dataFetchingError,
}: QuestionShowProps) => {
  if (dataFetchingError) return <PageSimplePage title={dataFetchingError.message}><BoundLink href='/' className='button is-large is-dark'>Return home</BoundLink></PageSimplePage>

  // Not yet loading fullBallot
  const {fullBallot, isLoadingFullBallot} = useBallotContext()
  const {voter} = useVoterContext()

  const boundRoute = useBoundRoute()
  const router = useRouter()
  const [isLoadingPage, setIsLoadingPage] = useState<boolean>(true)
  const [redirectMessage, setRedirectMessage] = useState<string | null>(null)

  // initialize all ballot-based state
  const [ballotIsPublished, setBallotIsPublished] = useState<boolean>(false)
  const [ballotIsPublic, setBallotIsPublic] = useState<boolean>(false)
  const [hasOnlyOneGroup, setHasOnlyOneGroup] = useState<boolean>(false)
  const [password, setPassword] = useState<string | null>(null)
  const [sponsorshipAd, setSponsorshipAd] = useState<SponsorshipAd | null>(null)
  // Initialize all group-based state
  const [allSeededDestinations, setAllSeededDestinations] = useState<SeededDestination[]>([])
  // Initialize all question-based state
  const [destination, setDestination] = useState<SeededDestination | null>(null)
  // Initialize all display ad state
  const [displayAd, setDisplayAd] = useState<CategoryAd | null>(null)
  const sendDisplayAd = (value: CategoryAd | null): void => setDisplayAd(value)
  const [displayAdPool, setDisplayAdPool] = useState<CategoryAd[]>([])
  const [displayAdIndex, setDisplayAdIndex] = useState<number>(0)
  const sendDisplayAdIndex = (value: number): void => setDisplayAdIndex(value)
  const [displayAdTimerDelay, setDisplayAdTimerDelay] = useState<number | null>(null)

  const [currentGroup, setCurrentGroup] = useState<ParentCategory | null>(null)
  const [groupBackgroundStyleObject, setGroupBackgroundStyleObject] = useState<CSSProperties>({})
  const [allFinalists, setAllFinalists] = useState<any>({})

  const initializeAllDestinationState = (
    {ballot, currentGroup, destination, analyticsSponsorshipAds}: InitializeDestinationStateParams,
  ) => {
    if (_.isEmpty(ballot) || !currentGroup || !destination) return
    setDestination(destination)

    const newDisplayAdPool = (destination.type === 'question') ? calculateDisplayAdPoolByBallotAndQuestion(ballot, destination) : []
    setDisplayAdPool(newDisplayAdPool)

    const newDisplayAd = calculateNextAdByPool(newDisplayAdPool)
    setDisplayAd(newDisplayAd)

    const newDisplayAdIndex = _.findIndex(newDisplayAdPool, ad => ad?._id === newDisplayAd?._id)
    setDisplayAdIndex(newDisplayAdIndex)

    let keenDisplayAds = newDisplayAd ? [newDisplayAd] : []
    // Only initialize timer (via delay) if there are multiple ads
    let newDisplayAdTimerDelay: number | null = null
    if ((ballot.general?.rotate_category_ads_text === 'rotate') && newDisplayAdPool?.length) {
      newDisplayAdTimerDelay = 5000
      keenDisplayAds = newDisplayAdPool
    }
    setDisplayAdTimerDelay(newDisplayAdTimerDelay)

    const pageviewProps = {
      ballotId: ballot._id,
      group: currentGroup,
      destination: destination as any,
      displayAds: keenDisplayAds,
      footerAds: null,
      sponsorshipAds: analyticsSponsorshipAds,
      componentName: 'QuestionShow' as const,
      ballot: ballot,
    }
    recordAdAndPageViews(pageviewProps)

    setIsLoadingPage(false)
  }
  // Initialize all session-based state
  const sendHasEnteredCorrectPassword = (isCorrect: boolean): void => {
    setCookie('_uhecp', String(isCorrect), {maxAge: convertHoursToSeconds(90), path: '/'})
    location.reload()
  }

  // Initialize resize handler based state
  const [isSqueezing, setIsSqueezing] = useState<boolean>(false)
  const [currentWindowHeight, setCurrentWindowHeight] = useState<number | null>(null)
  const [currentWindowWidth, setCurrentWindowWidth] = useState<number | null>(null)

  // Init all UI state
  const [sidebarIsOpen, setSidebarIsOpen] = useState<boolean>(false)
  const sendSidebarIsOpen = (value: boolean): void => setSidebarIsOpen(value)

  const [modalIsIn, setModalIsIn] = useState<boolean>(false)
  const sendModalIsIn = (value: boolean): void => setModalIsIn(value)

  const [currentModal, setCurrentModal] = useState<ReactNode | null>(null)
  const [currentModalTitle, setCurrentModalTitle] = useState<string | null>(null)
  const [currentModalHasCloseButton, setCurrentModalHasCloseButton] = useState<boolean | null>(null)
  const onUpdateModal = (
    modalContents: ReactNode | null = null, modalTitle: string | null = null, modalHasCloseButton: boolean = false,
  ) => {
    if (modalContents) {
      document.body.style.overflow = 'hidden'
      setCurrentModal(modalContents)
      setCurrentModalTitle(modalTitle)
      setCurrentModalHasCloseButton(modalHasCloseButton)
      setModalIsIn(true)
    } else {
      document.body.style.overflow = 'auto'
      setCurrentModal(null)
      setModalIsIn(false)
    }
  }

  const [[currentDestinationIndex, direction], setPage] = useState<[number, number]>([0, 0])
  const paginate = useCallback((newDirection: number) => {
    if (!router.isReady) return
    if (_.isEmpty(allSeededDestinations)) {
      router.push(boundRoute('/')!)
      return
    }

    const newDestinationIndex = currentDestinationIndex === -1 ? 0 : currentDestinationIndex + newDirection
    const nextDestination = typeof newDestinationIndex === 'number' ? allSeededDestinations[newDestinationIndex] : _.first(allSeededDestinations)
    // TODO: Update if sponsorshipAds can rotate
    const analyticsSponsorshipAds = sponsorshipAd ? [sponsorshipAd] : []

    initializeAllDestinationState({
      ballot: fullBallot,
      destination: nextDestination,
      currentGroup: currentGroup,
      analyticsSponsorshipAds,
    })

    const isNavigatingToIndex = (
      (newDirection < 0 && currentDestinationIndex === 0)
      || (newDirection > 0 && currentDestinationIndex === allSeededDestinations?.length - 1)
    )
    const groupSlug = router.query?.group_slug as string | undefined
    if (isNavigatingToIndex) {
      const isTrackingFrom = newDirection > 0
      const params = new URLSearchParams({from: groupSlug!})
      let boundIndexRoute: string | null | undefined = null
      const groups = fullBallot?.parent_categories || []
      const isLast = groups.length > 0 && currentGroup != null && groups[groups.length - 1]._id === currentGroup._id
      if (hasOnlyOneGroup || (isTrackingFrom && isLast)) {
        boundIndexRoute = boundRoute('/')
      } else {
        if (isTrackingFrom) {
          boundIndexRoute = removeTrailingSlash(boundRoute(`/groups?${params}`))
        } else {
          boundIndexRoute = boundRoute('/groups')
        }
      }
      router.push(boundIndexRoute!)
      return
    }
    setPage([newDestinationIndex, newDirection])

    const destinationIsAd = nextDestination?.type === 'ad'

    const questionOrAdUrlPart = destinationIsAd ? `--ad-${nextDestination?._id}` : slugify(nextDestination?.name)
    const newPathname = boundRoute(`/${groupSlug}/${questionOrAdUrlPart}`)
    router.push(newPathname!, undefined, {shallow: true})
  }, [
    router.query,
    router.push,
    router.isReady,
    boundRoute,
    allSeededDestinations,
    currentDestinationIndex,
    fullBallot,
    sponsorshipAd,
    currentGroup,
    hasOnlyOneGroup,
  ])
  const onPaginate = (value: number) => paginate(value)

  const handleResize = useCallback(() => {
    const newCurrentWindowHeight = window.innerHeight
    setCurrentWindowHeight(newCurrentWindowHeight)

    const newCurrentWindowWidth = window.innerWidth
    setCurrentWindowWidth(newCurrentWindowWidth)

    setIsSqueezing(newCurrentWindowWidth > 480)
  }, [])

  useEffect(() => {
    // Initialize once on mount
    handleResize()
    // Attach and clean up the window resize listener
    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [handleResize])

  useEffect(() => {
    if (!router.isReady) return
    let mounted = true
    if (mounted && !isLoadingFullBallot && fullBallot) {
      if (_.isEmpty(fullBallot)) return
      setCookie('ballot_id', fullBallot._id, {maxAge: convertHoursToSeconds(90), path: '/'})

      const ballotDisplayType = accessBallotDisplayType(fullBallot)
      if (ballotDisplayType === 'landing-page-only') {
        router.push(boundRoute('/')!)
        return
      }

      // TODO: Create separate functions for each of these for readability
      //
      // This useEffect sets up all page state based on the fullBallot and URL params
      // Sets ads and general ballot state
      // Sets current group
      // Sets background style object
      // Sets all finalists
      // Sets all seeded destinations
      // Sets page

      //
      // Set ads and general ballot state
      const isQuestionShow = true
      const sponsorshipAdPool = calculateSponsorshipAdPoolByBallot(fullBallot, isQuestionShow)
      const newSponsorshipAd = calculateNextAdByPool(sponsorshipAdPool ?? [])
      setSponsorshipAd(newSponsorshipAd)

      const analyticsSponsorshipAds: SponsorshipAd[] = newSponsorshipAd ? [newSponsorshipAd] : []

      const newBallotIsPublished = accessBallotIsPublished(fullBallot)
      setBallotIsPublished(newBallotIsPublished)

      const newBallotIsPublic = accessBallotIsPublic(fullBallot) || (getCookie('_uhecp') === 'true')
      setBallotIsPublic(newBallotIsPublic)

      const newHasOnlyOneGroup = calculateHasOnlyOneGroup(fullBallot)
      setHasOnlyOneGroup(newHasOnlyOneGroup)

      const newPassword = calculateBallotPassword(fullBallot)
      setPassword(newPassword ?? null)

      //
      // Set current group
      setRedirectMessage(null)
      const currentGroupSlug = router.query?.group_slug as string | undefined
      if (!currentGroupSlug) {
        setRedirectMessage('This group does not exist in this ballot')
        setIsLoadingPage(false)
        return
      }

      const newCurrentGroup = calculateGroupByBallotAndGroupSlug(fullBallot, currentGroupSlug)
      if (!newCurrentGroup) {
        setCurrentGroup(null)
        const allGroups = fullBallot?.parent_categories || []
        const matchedGroupCount = allGroups.filter(group => slugify(group.parent_category_name) === currentGroupSlug).length
        setRedirectMessage(
          matchedGroupCount > 1
            ? 'This group URL is ambiguous — multiple groups share this slug in this ballot'
            : 'This group does not exist in this ballot',
        )
        setIsLoadingPage(false)
        return
      }
      setCurrentGroup(newCurrentGroup ?? null)

      //
      // Set background style object
      const ballotGroupBackgroundUrl = fullBallot.general?.group_background_image
      const groupBackgroundUrl = newCurrentGroup?.parent_images?.groupBackgroundImagePath
      const newGroupBackgroundUrl = groupBackgroundUrl || ballotGroupBackgroundUrl

      let newGroupBackgroundStyleObject: CSSProperties = {}
      if (newGroupBackgroundUrl) {
        newGroupBackgroundStyleObject = {backgroundImage: `url(${newGroupBackgroundUrl})`}
        const isTilingGroupBackground = newCurrentGroup?.parent_images?.group_background_repeat_text === 'repeat'
        if (isTilingGroupBackground) {
          newGroupBackgroundStyleObject['backgroundRepeat'] = 'repeat'
        } else {
          newGroupBackgroundStyleObject['backgroundSize'] = 'cover'
        }
      }
      setGroupBackgroundStyleObject(newGroupBackgroundStyleObject)

      //
      // Set all seeded destinations
      const checkIsImageUrlGroupUrl = (url: unknown): boolean => {
        if (typeof url !== 'string') return false
        // TODO: Validate format of url string
        return _.includes(url, '~') && !_.includes(url, 'nth')
      }
      const buildPhotoImagesArray = (photoUrl: string | undefined): string[] => {
        if (!photoUrl) return []
        if (!checkIsImageUrlGroupUrl(photoUrl)) return [photoUrl]

        const splitUrl = photoUrl.split('/')
        const groupInfoUri = splitUrl[splitUrl.length - 2]
        const tildeIndex = groupInfoUri.lastIndexOf('~')
        const count = (
          tildeIndex === -1
            ? 0
            : Number(groupInfoUri.slice(tildeIndex + 1)) || 0
        )
        if (count <= 0) return []

        return Array.from(
          {length: count},
          (_, i) => `https://ucarecdn.com/${groupInfoUri}/nth/${i}/`,
        )
      }
      const ballotAdditionalFormFields = fullBallot.photo_submission_settings?.additional_form_fields || []
      const ballotFieldsById = new Map<string, any>()
      const selectFieldRankMaps = new Map<string, Map<any, any>>()

      ballotAdditionalFormFields.forEach((field: any) => {
        if (!field || !field.id) return
        ballotFieldsById.set(field.id, field)
        if (field.type === 'select' && Array.isArray(field.drop_down_choices)) {
          const rankMap = new Map<any, any>()
          for (const option of field.drop_down_choices) {
            if (option && typeof option.rank !== 'undefined') {
              rankMap.set(option.rank, option)
            }
          }
          selectFieldRankMaps.set(field.id, rankMap)
        }
      })

      const newAllSeededDestinations = seedInterstitialAdsByBallotfulGroup({...newCurrentGroup, ballot: fullBallot} as any)
      setAllSeededDestinations(newAllSeededDestinations ?? [])

      const destinationIdentifier = router.query?.question_slug as string | undefined
      const newDestination = calculateDestinationByDestinationsAndIdentifier(
        newAllSeededDestinations ?? [], destinationIdentifier,
      )
      if (!newDestination) {
        setDestination(null)
        setIsLoadingPage(false)
        return
      }
      initializeAllDestinationState({
        ballot: fullBallot,
        currentGroup: newCurrentGroup ?? null,
        destination: newDestination,
        analyticsSponsorshipAds,
      })

      //
      // Set all finalists
      const allRawQuestions = newCurrentGroup?.sub_categories || null

      const allQuestions = makeQuestionsForDisplay(allRawQuestions, currentGroupSlug)
      const allSortedQuestions = (newCurrentGroup?.sort_order === 'manual') ? allQuestions : _.sortBy(allQuestions, (question: any) => question.name.toLowerCase())

      const allQuestionEntries: any[] = []
      allSortedQuestions?.forEach((question: any) => {
        const questionEntries = question.final_lists.map((entry: any, index: number) => {
          const additionalFormFields = (
            Array.isArray(entry.additional_form_fields)
              ? (
                entry.additional_form_fields.map((rawField: any) => {
                  const ballotField = ballotFieldsById.get(rawField.id)
                  if (!ballotField) {
                    return {
                      id: rawField.id,
                      value: rawField.value,
                      type: undefined,
                      field_label: undefined,
                      hide_field_in_voting_and_gallery_view: false,
                    }
                  }

                  const newAdditionalFormField: any = {
                    id: rawField.id,
                    value: rawField.value,
                    type: ballotField.type,
                    field_label: ballotField.name,
                    hide_field_in_voting_and_gallery_view: ballotField.hide_field_in_voting_and_gallery_view || false,
                  }

                  if (ballotField.type === 'select') {
                    const rankMap = selectFieldRankMaps.get(ballotField.id)
                    const optionObject = rankMap ? rankMap.get(rawField.value) : undefined
                    return {
                      ...newAdditionalFormField,
                      option_name: optionObject || undefined,
                    }
                  }
                  return newAdditionalFormField
                })
              )
              : []
          )
          entry['additional_form_fields'] = additionalFormFields
          const photoImagesArray = buildPhotoImagesArray(entry.photo_image)
          entry['photo_images_array'] = photoImagesArray
          entry['cover_image'] = photoImagesArray[0] || null
          entry['key'] = `${question.slug}-${index}`
          entry['type'] = 'option'
          entry['question_slug'] = question.slug
          entry['group_id'] = newCurrentGroup?._id
          entry['sub_category_id'] = question._id
          return entry
        })
        allQuestionEntries.push(...questionEntries)
      })

      const newAllFinalists = (
        ballotDisplayType === 'photo-voting-results'
          ? _.orderBy(allQuestionEntries, [entry => parseInt(entry.votes_count, 10)], ['desc'])
          : shuffle(allQuestionEntries)
      )
      setAllFinalists(newAllFinalists)

      //
      // Set page
      const destinationIndex = _.findIndex(
        newAllSeededDestinations, (dest: SeededDestination) => dest._id === newDestination?._id,
      )
      const newDestinationIndex = destinationIndex
      setPage([newDestinationIndex, 0])
    }
    return () => {
      mounted = false
    }
  }, [fullBallot, router.isReady])

  const galleryType = initBasicBallot.photo_display_settings?.gallery_type || 'photo-carousel'

  // Navigation display state
  const ballotDisplayType = fullBallot ? accessBallotDisplayType(fullBallot) : null
  const isActiveInteractionMode = ballotDisplayType === 'photo-voting-vote' || ballotDisplayType === 'photo-submissions-submit'
  const isViewingQuestion = destination?.type === 'question'
  const questionDestinationCount = allSeededDestinations.filter(d => d.type === 'question').length
  const isOnlySubcategory = questionDestinationCount === 1
  const isLastDestination = allSeededDestinations.length > 0 && currentDestinationIndex === allSeededDestinations.length - 1
  const isLastGroup = useMemo(() => {
    if (!fullBallot || !currentGroup) return false
    const groups = fullBallot.parent_categories || []
    return groups.length > 0 && groups[groups.length - 1]._id === currentGroup._id
  }, [fullBallot, currentGroup])
  const hasInteracted = useMemo(() => {
    if (!isViewingQuestion || !destination) return false
    const allInteractions = voter?.votes ?? []
    const allSubmissions = voter?.submissions ?? []
    return allInteractions.some(v => v.sub_category_id === destination._id)
      || allSubmissions.some(v => v.sub_category_id === destination._id)
  }, [
    isViewingQuestion, destination, voter?.votes, voter?.submissions,
  ])

  return <>
    <ClientMeta ballot={initBasicBallot || backupBallot} pageTitle={`${initBasicBallot.ballot_name || backupBallot.ballot_name} - Home`} />
    {isLoadingPage && <LoadingIndicator />}
    {!isLoadingPage && _.isEmpty(fullBallot) && (
      <PageSimplePage title='The ballot you are looking for does not exist'>
        <h2 className='title is-4 pb-2'>
          Try returning to the home page and signing in again
        </h2>
        <BoundLink
          href='/'
          className='button is-large is-dark'
        >
          OK
        </BoundLink>
      </PageSimplePage>
    )}
    {!isLoadingPage && !_.isEmpty(fullBallot) && <>
      {ballotIsPublished && <>
        {ballotIsPublic && <>
          {redirectMessage && (
            <PageSimplePage title={redirectMessage}>
              <BoundLink
                href='/'
                className='button is-large is-dark'
              >
                OK
              </BoundLink>
            </PageSimplePage>
          )}
          {!redirectMessage && <>
            <ClientHead ballot={fullBallot} />
            {/* Question or ad display */}
            <div
              className={`
                ${pageStyles['page']}
                ${pageStyles['--is-fixed-height']}
                ${(sidebarIsOpen && isSqueezing) ? `${pageStyles['--is-squeezing']}` : ''}
                ${modalIsIn ? pageStyles['--modal-is-in'] : ''}
              `}
            >
              {/* Main navigation */}
              <Navbar
                customer={(initBasicBallot.customer ?? null) as Customer | null}
                ballot={fullBallot}
                currentWindowWidth={currentWindowWidth ?? 0}
                ballotIsPublished={true}
                sidebarIsOpen={sidebarIsOpen}
                sendSidebarIsOpen={sendSidebarIsOpen}
                onPaginate={onPaginate}
                onUpdateModal={onUpdateModal}
              />
              {/* Animated question or interstitial ad */}
              {_.isEmpty(destination)
                ? (
                  <PageSimplePage title='This sub-category does not exist in this ballot'>
                    <h2 className='title is-4'>Please use the "Browse" navigation to find one that does</h2>
                  </PageSimplePage>
                )
                : (
                  <div
                    role="main"
                    className={pageStyles['page-question-or-ad-show']}
                    style={modalIsIn ? {zIndex: '20'} : {}}
                  >
                    <AnimationLinearFade
                      currentIndex={currentDestinationIndex}
                      direction={direction}
                      isShowingInterstitialAd={destination!.type === 'ad'}
                    >
                      {destination!.type === 'ad' &&
                        <InterstitialAd
                          interstitialAd={destination}
                          ballot={fullBallot}
                          group={currentGroup}
                          hasSponsorshipAd={Boolean(sponsorshipAd)}
                          onPaginate={onPaginate}
                        />
                      }
                      {/* TODO: Refactor these into <PhotoGallery /> Which houses each gallery type, but handles the overarching logic */}
                      {
                        destination!.type === 'question'
                        && accessBallotDisplayType(fullBallot) !== 'landing-page-only'
                        && <PhotoGallery
                          customer={initBasicBallot.customer ?? null}
                          ballot={fullBallot}
                          galleryType={galleryType}
                          displayAd={displayAd}
                          displayAdPool={displayAdPool}
                          displayAdIndex={displayAdIndex}
                          displayAdTimerDelay={displayAdTimerDelay}
                          currentQuestion={destination}
                          currentGroup={currentGroup!}
                          allFinalists={allFinalists}
                          rankDictionary={initBasicBallot.rankDictionary ?? null}
                          currentWindowHeight={currentWindowHeight ?? 0}
                          currentWindowWidth={currentWindowWidth ?? 0}
                          hasSponsorshipAd={Boolean(sponsorshipAd)}
                          sendDisplayAd={sendDisplayAd}
                          sendDisplayAdIndex={sendDisplayAdIndex}
                          sendModalIsIn={sendModalIsIn}
                          onPaginate={onPaginate}
                        />
                      }
                    </AnimationLinearFade>
                  </div>
                )
              }
              {/* Question/ad navigation */}
              <SponsoredFooter
                ballotId={fullBallot._id}
                sponsorshipAd={sponsorshipAd}
                isSqueezed={sidebarIsOpen && isSqueezing}
                onPaginate={onPaginate}
                isOnlySubcategory={isOnlySubcategory}
                isLastDestination={isLastDestination}
                hasInteracted={hasInteracted}
                isViewingQuestion={isViewingQuestion}
                isActiveInteractionMode={isActiveInteractionMode}
                hasOnlyOneGroup={hasOnlyOneGroup}
                isLastGroup={isLastGroup}
              />
              {groupBackgroundStyleObject && (typeof groupBackgroundStyleObject === 'object') && (Object.keys(groupBackgroundStyleObject).length > 0) &&
                <div
                  style={groupBackgroundStyleObject}
                  className={pageStyles['__background-image']}
                ></div>
              }
            </div>
            {modalIsIn && currentModal &&
              <ModalContainer
                modalTitle={currentModalTitle ?? undefined}
                modalHasCloseButton={currentModalHasCloseButton ?? undefined}
                onUpdateModal={onUpdateModal}
              >
                {currentModal}
              </ModalContainer>
            }
          </>}
        </>}
        {!ballotIsPublic &&
          <PagePasswordInput ballotPassword={password ?? ''} sendHasEnteredCorrectPassword={sendHasEnteredCorrectPassword} />
        }
      </>}
      {!ballotIsPublished &&
        <PageSimplePage title='This ballot is no longer active'>
          <BoundLink
            href='/'
            className='button is-large is-dark'
          >
            Read more
          </BoundLink>
        </PageSimplePage>
      }
    </>}
  </>
}

export default QuestionShow

export const getServerSideProps = async (
  context: GetServerSidePropsContext,
): Promise<GetServerSidePropsResult<QuestionShowProps>> => {
  const {initBasicBallot, prerenderError, customRedirect} = await loadBallotByHost(context) as any
  if (prerenderError) return prerenderError
  if (customRedirect) return customRedirect

  return {props: {initBasicBallot}}
}
